import { useSWRMutation } from '@tiger/swr';
import { Button } from 'antd';
import classNames from 'classnames';
import { isUndefined } from 'lodash';
import type { FC } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import BottomBox from 'app/components/BottomBox';
import InvalidRedirect from 'components/InvalidRedirect';
import ScrollTop from 'components/ScrollTop';
import Toast from 'components/Toast';
import security from 'stores/security';
import API from 'utils/API';
import { formatMoney } from 'utils/format';
import http from 'utils/http';
import { getMaskText } from 'utils/util';
import { RecurringScheduleStatus } from '../constants';
import type { ACHDepositRecordTypes } from '../types';
import styles from './style.scss';

interface RecordDetailProps {
    isIRA: boolean;
}

const RecordDetail: FC<RecordDetailProps> = ({ isIRA }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams<{ id: string }>();
    const record = (location.state as { record: ACHDepositRecordTypes } | null)?.record;

    const descs = [
        {
            label: __('申请时间'),
            detail: record?.createdAt ? dayjs(record.createdAt).format('YYYY-MM-DD HH:mm') : '- -'
        },
        {
            label: __('币种'),
            detail: record?.currency ?? '- -'
        },
        {
            label: isIRA ? __('Contribution Amount') : __('Deposit Amount'),
            detail: !isUndefined(record?.amount) ? formatMoney(record.amount) : '- -'
        },
        {
            label: __('银行账户'),
            detail: record?.account ? getMaskText(record.account) : '- -'
        },
        {
            label: __('转账方式'),
            detail: record?.transactionWay ?? '- -'
        },
        {
            label: __('银行参考号'),
            detail: record?.bankReference ?? '- -'
        }
    ];

    const { trigger: handleCancel, isMutating: isCanceling } = useSWRMutation(
        {
            url: API.TRADE.funding.cancelDepositAchPlan(record!.id),
            params: {
                account_id: security.currentAccountId,
                customer_id: security.currentCustomerId
            }
        },
        () => {
            return http.post(API.TRADE.funding.cancelDepositAchPlan(record!.id), undefined, {
                params: {
                    account_id: security.currentAccountId,
                    customer_id: security.currentCustomerId
                }
            });
        },
        {
            onError: err => {
                Toast.show(err);
            },
            onSuccess: () => {
                Toast.show(__('取消成功'));
                navigate(`/profile/deposit/ach/recurring/schedules/${params.id}`, { replace: true });
            }
        }
    );

    if (!record) {
        return (
            <InvalidRedirect
                to={`/profile/deposit/ach/recurring/schedules/${params.id}`}
                errorMsg="No schedule record."
            />
        );
    }

    return (
        <>
            <ScrollTop />
            <div className={styles.pageRoot}>
                <h2 className={styles.pageTitle}>
                    {__('状态')}
                    <span>{RecurringScheduleStatus[record.status]?.text || record.status}</span>
                </h2>
                {descs.map(desc => (
                    <p key={desc.label} className={styles.detailItem}>
                        <label>{desc.label}</label>
                        <span>{desc.detail}</span>
                    </p>
                ))}
                <BottomBox className="mt-5">
                    <Button
                        block
                        type={record.cancelable ? 'default' : 'primary'}
                        className="btn-important"
                        onClick={() => navigate(-1)}>
                        {__('Back')}
                    </Button>
                    {record.cancelable && (
                        <Button
                            block
                            type="primary"
                            loading={isCanceling}
                            onClick={() => handleCancel()}
                            className={classNames(styles.cancelBtn, 'btn-important mt-3')}>
                            {__('取消入金')}
                        </Button>
                    )}
                </BottomBox>
            </div>
        </>
    );
};

export default RecordDetail;
